import { invariantResponse } from '@epic-web/invariant'
import {
	json,
	type MetaFunction,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ProductsList } from '#app/components/ui/products-list-cards.tsx'
import { SITE_FULL_TITLE } from '#app/utils/client-constants.js'
import { prisma } from '#app/utils/db.server.js'

export async function loader({ params }: LoaderFunctionArgs) {
	const user = await prisma.user.findFirst({
		where: {
			username: params.username,
		},
		select: {
			id: true,
			name: true,
			addedProducts: {
				orderBy: {
					createdAt: 'desc',
				},
				select: {
					id: true,
					name: true,
					slug: true,
					images: {
						select: {
							id: true,
							altText: true,
						},
					},
					category: {
						select: {
							slug: true,
						},
					},
				},
			},
		},
	})

	invariantResponse(user, 'User not found', { status: 404 })

	return json({
		user,
	})
}

export default function ProductsAdded() {
	const data = useLoaderData<typeof loader>()
	const addedProductsCount = data.user.addedProducts.length
	return (
		<div className="flex flex-col gap-8">
			<div className="flex w-full items-center justify-between">
				<h5 className="text-lg text-foreground">
					<span className="font-bold">{addedProductsCount}</span>{' '}
					<span>{addedProductsCount === 1 ? 'product' : 'products'} added</span>
				</h5>
			</div>
			<ProductsList
				products={data.user.addedProducts.filter(product => product !== null)}
			/>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	const contentTitle = 'Products Added'

	return [
		{ title: `${displayName}'s ${contentTitle} | ${SITE_FULL_TITLE}` },
		{
			name: 'description',
			content: `See ${displayName}'s ${contentTitle} on ${SITE_FULL_TITLE}`,
		},
	]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
